import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { euros, fecha, plural } from "../lib/formato";
import { Aviso, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";

interface Licencia {
  id: string;
  codigo: string;
  estado: "DISPONIBLE" | "USADA" | "ANULADA" | "CADUCADA";
  cuotaMensualCentimos: number;
  diasPrueba: number;
  notas: string | null;
  caducaEn: string | null;
  usadaEn: string | null;
  createdAt: string;
  club: { id: string; nombre: string; slug: string } | null;
}

interface Respuesta {
  licencias: Licencia[];
  resumen: { total: number; disponibles: number; usadas: number };
  /** Los días de prueba que anuncia la web: lo que propone el formulario. */
  diasDePrueba: number;
}

const ETIQUETA: Record<Licencia["estado"], { texto: string; clase: string }> = {
  DISPONIBLE: { texto: "Sin usar", clase: "verde" },
  USADA: { texto: "Canjeada", clase: "gris" },
  ANULADA: { texto: "Anulada", clase: "roja" },
  CADUCADA: { texto: "Caducada", clase: "ambar" },
};

/**
 * Licencias: los códigos que se venden.
 *
 * Un club con un código se da de alta solo, y el plan que llevaba dentro
 * (cuota y días de prueba) se le aplica al momento. Vender el código y dejar
 * al club configurado son el mismo acto.
 */
export default function Licencias() {
  const cliente = useQueryClient();
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState("");
  const [copiado, setCopiado] = useState("");

  const datos = useQuery({ queryKey: ["plataforma-licencias"], queryFn: () => api<Respuesta>("/plataforma/licencias") });
  const refrescar = () => void cliente.invalidateQueries({ queryKey: ["plataforma-licencias"] });

  async function anular(l: Licencia) {
    setError("");
    try {
      await api(`/plataforma/licencias/${l.id}/anular`, { metodo: "POST" });
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo anular");
    }
  }

  async function copiar(codigo: string) {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(codigo);
      setTimeout(() => setCopiado(""), 2000);
    } catch {
      // Sin permiso del navegador el código se ve igual en pantalla.
    }
  }

  if (datos.isLoading || !datos.data) return <Cargando />;
  const { licencias, resumen } = datos.data;

  return (
    <div>
      <div className="fila entre" style={{ marginBottom: 16 }}>
        <div className="rejilla tres" style={{ flex: 1, marginRight: 12 }}>
          <TarjetaDato etiqueta="Sin usar" valor={resumen.disponibles} pie="Listas para vender" />
          <TarjetaDato etiqueta="Canjeadas" valor={resumen.usadas} pie="Clubes que nacieron de una" />
          <TarjetaDato etiqueta="Emitidas" valor={resumen.total} pie="En total" />
        </div>
        <button className="boton" onClick={() => setGenerando(true)}>
          + Licencias
        </button>
      </div>

      <Aviso tipo="error">{error}</Aviso>

      <Panel sinCuerpo>
        {licencias.length === 0 ? (
          <Vacio>
            Todavía no has emitido ninguna licencia. Genera un lote y reparte los códigos: quien tenga uno podrá
            darse de alta sin que tú tengas que crearle nada.
          </Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Estado</th>
                  <th>Plan</th>
                  <th>Para quién</th>
                  <th>Club</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {licencias.map((l) => {
                  const et = ETIQUETA[l.estado];
                  return (
                    <tr key={l.id} style={l.estado === "DISPONIBLE" ? undefined : { opacity: 0.65 }}>
                      <td>
                        <button
                          className="boton enlace tabular"
                          onClick={() => void copiar(l.codigo)}
                          title="Copiar el código"
                        >
                          {l.codigo}
                        </button>
                        {copiado === l.codigo && <span className="pequeno verde"> copiado</span>}
                        <div className="suave pequeno">emitida el {fecha(l.createdAt)}</div>
                      </td>
                      <td>
                        <Etiqueta tipo={et.clase}>{et.texto}</Etiqueta>
                        {l.estado === "DISPONIBLE" && l.caducaEn && (
                          <div className="suave pequeno">vale hasta {fecha(l.caducaEn)}</div>
                        )}
                      </td>
                      <td className="pequeno">
                        {l.cuotaMensualCentimos > 0 ? `${euros(l.cuotaMensualCentimos)}/mes` : "Sin cuota"}
                        <div className="suave">
                          {l.diasPrueba > 0 ? `${plural(l.diasPrueba, "día")} de prueba` : "Sin prueba"}
                        </div>
                      </td>
                      <td className="pequeno suave">{l.notas ?? "—"}</td>
                      <td className="pequeno">
                        {l.club ? (
                          <>
                            {l.club.nombre}
                            <div className="suave">canjeada el {fecha(l.usadaEn)}</div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {l.estado === "DISPONIBLE" && (
                          <button className="boton fantasma" onClick={() => void anular(l)}>
                            Anular
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {generando && (
        <DialogoGenerar
          diasDePrueba={datos.data?.diasDePrueba ?? 0}
          alCerrar={() => setGenerando(false)}
          alFallar={setError}
          alHecho={() => {
            setGenerando(false);
            setError("");
            refrescar();
          }}
        />
      )}
    </div>
  );
}

function DialogoGenerar({
  diasDePrueba,
  alCerrar,
  alHecho,
  alFallar,
}: {
  diasDePrueba: number;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const [datos, setDatos] = useState({
    cantidad: "1",
    cuotaMensualEuros: "",
    diasPrueba: String(diasDePrueba),
    notas: "",
    caducaEn: "",
  });
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string) => setDatos((d) => ({ ...d, [campo]: valor }));

  return (
    <Modal
      titulo="Generar licencias"
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/plataforma/licencias", {
                  metodo: "POST",
                  cuerpo: {
                    cantidad: Number(datos.cantidad || 1),
                    cuotaMensualEuros: Number(datos.cuotaMensualEuros || 0),
                    diasPrueba: Number(datos.diasPrueba || 0),
                    notas: datos.notas || null,
                    caducaEn: datos.caducaEn || null,
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : "No se pudieron generar");
              } finally {
                setEnviando(false);
              }
            }}
          >
            {enviando ? "Generando..." : "Generar"}
          </button>
        </>
      }
    >
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        Cada código sirve una sola vez. Quien lo tenga podrá dar de alta su club sin que tú hagas nada, y se le
        aplicará el plan que pongas aquí.
      </p>

      <div className="rejilla dos">
        <Campo etiqueta="Cuántas" ayuda="Un lote para un distribuidor, o una para un club concreto.">
          <input
            type="number"
            min="1"
            max="100"
            value={datos.cantidad}
            onChange={(e) => cambiar("cantidad", e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Cuota mensual (EUR)" ayuda="Lo que pagará ese club al mes.">
          <input
            type="number"
            step="0.01"
            min="0"
            value={datos.cuotaMensualEuros}
            onChange={(e) => cambiar("cuotaMensualEuros", e.target.value)}
          />
        </Campo>
      </div>

      <div className="rejilla dos">
        <Campo etiqueta="Días de prueba" ayuda="Antes de empezar a cobrar. 0 = sin prueba.">
          <input
            type="number"
            min="0"
            max="365"
            value={datos.diasPrueba}
            onChange={(e) => cambiar("diasPrueba", e.target.value)}
          />
        </Campo>
        <Campo etiqueta="El código caduca el" ayuda="Opcional. Si no se canjea antes, deja de valer.">
          <input type="date" value={datos.caducaEn} onChange={(e) => cambiar("caducaEn", e.target.value)} />
        </Campo>
      </div>

      <Campo etiqueta="Para quién" ayuda="Para acordarte de a quién se lo diste. El club no lo ve.">
        <input
          value={datos.notas}
          onChange={(e) => cambiar("notas", e.target.value)}
          placeholder="Feria de Barcelona, distribuidor Norte..."
        />
      </Campo>
    </Modal>
  );
}
